import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { DialogShellNav, type DialogShellNavSection } from '@bliks-io/ui';

export interface TypePickerNavOption<K extends string> {
  value: K;
  icon: LucideIcon;
  label: string;
}

export interface TypePickerNavSectionInput<K extends string> {
  id: string;
  heading?: string;
  options: TypePickerNavOption<K>[];
}

interface TypePickerNavProps<K extends string> {
  sections: TypePickerNavSectionInput<K>[];
  currentType: K;
  onSelect: (type: K) => void;
}

/**
 * Left rail of a master-detail picker modal: one card-style nav item per
 * entity type. Shared by the successor picker and the helpers picker.
 */
export function TypePickerNav<K extends string>({
  sections,
  currentType,
  onSelect
}: TypePickerNavProps<K>) {
  const navSections = useMemo<DialogShellNavSection[]>(
    () =>
      sections.map((section) => ({
        id: section.id,
        heading: section.heading,
        items: section.options.map(({ value, icon, label }) => ({
          id: value,
          icon,
          label,
          active: currentType === value,
          onSelect: () => onSelect(value)
        }))
      })),
    [sections, currentType, onSelect]
  );

  return <DialogShellNav sections={navSections} activeStyle="card" />;
}
